<script>
import { cloneDeep, debounce } from 'lodash'
import baseMixin from '@/mixins/baseMixin'
import { ShortTermStatus } from '@/constants/booking/ShortTermStatus'
import DataPoint from '@/models/general/dataPoint'
import { mapGetters } from 'vuex'
import { OrganisationLevel } from '@/constants/admin/OrganisationLevel'
import StatisticsBreakdownCard from '../general/statistics-breakdown-card.vue'
import OrganisationTreeSelector from '../general/organisation-tree-selector.vue'

export default {
  name: 'DisputesTableOverviewStatsBreakdownCard',
  components: { StatisticsBreakdownCard, OrganisationTreeSelector },
  mixins: [baseMixin],
  props: {
    disputes: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      adminIds: [],
      filterModel: null,
      graphWidth: 160,
    }
  },
  computed: {
    ...mapGetters({
      isLoadingBookingDisputes: 'disputes/isLoading',
      currentUser: 'auth/currentUser',
      staffByAdminIds: 'staff/getStaffByIds',
      allowedToViewAdminIds: 'disputes/getAllowedToViewAdminIds',
      organisationTree: 'staff/organisationTree',
    }),
    autocompleteStaff() {
      const staff = this.staffByAdminIds(this.allowedToViewAdminIds)
      return staff.map((a) => ({
        text: a.name,
        value: a.id,
      }))
    },
    autocompleteSelectedModel: {
      get() {
        return this.filterModel
      },
      set(val) {
        this.filterModel = val
      },
    },
    graphData() {
      const dataPoints = this.getDefaultGraphDataObj()

      const [
        notSubmittedSummaryIndex,
        modifiedSummaryIndex,
        otherSummaryIndex,
      ] = [...Array(dataPoints.length).keys()]

      return this.disputes.reduce((accumulator, item) => {
        switch (item.bookingStatus) {
          case ShortTermStatus.BOOKING_NOT_APPROVED:
          case ShortTermStatus.ECH_BOOKING_NOT_APPROVED: {
            accumulator[notSubmittedSummaryIndex].value++
            break
          }

          case ShortTermStatus.CANDIDATE_SUBMITTED_TIMESHEET_MODIFIED:
          case ShortTermStatus.CLIENT_SUBMITTED_TIMESHEET_MODIFIED:
          case ShortTermStatus.CLIENT_SUBMITTED_TIMESHEET_DID_NOT_HAPPEN:
          case ShortTermStatus.CANDIDATE_SUBMITTED_TIMESHEET_DID_NOT_HAPPEN:
          case ShortTermStatus.TIMESHEET_DISPUTE_OR_MISMATCH: {
            accumulator[modifiedSummaryIndex].value++
            break
          }

          case ShortTermStatus.CANDIDATE_SUBMITTED_TIMESHEET_NO_CHANGES:
          case ShortTermStatus.CLIENT_SUBMITTED_TIMESHEET_NO_CHANGES: {
            accumulator[otherSummaryIndex].value++
            break
          }
        }

        return accumulator
      }, dataPoints)
    },
    compGraphOptions() {
      return {
        cutOut: 30,
        layout: {
          padding: {
            left: this.graphWidth / 2,
          },
        },
      }
    },
  },
  watch: {
    autocompleteSelectedModel: debounce(async function (newValue, oldValue) {
      this.updateSelectedFilter()
    }, 500),
  },
  methods: {
    getDefaultGraphDataObj() {
      return cloneDeep([
        new DataPoint({
          key: 'dispute_stats_not_submitted',
          title: 'Not Submitted',
          value: 0,
          showSummary: true,
          showOnGraph: true,
          color: '#FF6384', // Red
        }),
        new DataPoint({
          key: 'dispute_stats_modified',
          title: 'Modified',
          value: 0,
          showSummary: true,
          showOnGraph: true,
          color: '#FFCE56', // Yellow
        }),
        new DataPoint({
          key: 'dispute_stats_other',
          title: 'Other',
          value: 0,
          showSummary: true,
          showOnGraph: true,
          color: '#00ADEF', // Blue
        }),
      ])
    },
    updateSelectedFilter() {
      return this.$emit('filterUpdated', this.filterModel)
    },
    filterUpdated(selection) {
      // Treeview select will contain a list of admin nodes
      if (Array.isArray(selection)) {
        this.filterModel = {
          type: OrganisationLevel.Admin,
          ids: selection.map((selectedNode) => selectedNode.id),
        }
        return
      }

      // One of the action buttons was selected
      switch (selection.type) {
        case 'Mine': {
          this.filterModel = {
            type: OrganisationLevel.Admin,
            ids: [this.currentUser.id],
          }
          break
        }
        default: {
          this.filterModel = { type: selection.type, ids: [selection.id] }
          break
        }
      }
    },
    /**
     * @param {string[]} payload List of selected categories
     */
    handleFilterChange(payload) {
      this.$emit('filterChanged', payload)
    },
  },
}
</script>

<template>
  <StatisticsBreakdownCard
    v-if="graphData"
    class="h-100 w-100"
    :data-points="graphData"
    :graph-options="compGraphOptions"
    filter
    @filterChanged="handleFilterChange"
  >
    <template v-slot:header>
      <OrganisationTreeSelector
        multiple
        searchable
        dense
        order-actions="desc"
        preselect="Mine"
        @update="filterUpdated"
      />
    </template>
  </StatisticsBreakdownCard>
</template>
